/* HHS Collection page interactivity — vanilla, scoped. */
(function () {
  'use strict';

  /* ─── Guard facets.js's renderAdditionalElements ───
     facets.js (theme-native) calls
       document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
     unconditionally at the end of `renderAdditionalElements`. Our mobile
     filter drawer is NOT wrapped in a `<menu-drawer>` custom element
     (we use our own `.filter-drawer` markup), so `.closest('menu-drawer')`
     returns null and `.bindEvents()` throws.

     Because `renderAdditionalElements` is invoked from inside
     `renderFilters` which runs BEFORE `renderProductGridContainer` in
     `renderSectionFromFetch`, the throw aborts the whole render
     pipeline and the product grid is never swapped — exactly the bug
     reported ("filter action happens but products don't update").

     Fix: wrap the static method so we run the SAME mobile-element copy
     loop facets.js runs, then only call `bindEvents()` when a real
     `<menu-drawer>` ancestor exists. No facets.js changes. */
  function patchFacetsRenderAdditionalElements() {
    if (typeof FacetFiltersForm === 'undefined') return false;
    if (FacetFiltersForm._hhsPatched) return true;
    FacetFiltersForm.renderAdditionalElements = function (html) {
      var mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];
      mobileElementSelectors.forEach(function (selector) {
        if (!html.querySelector(selector)) return;
        var target = document.querySelector(selector);
        if (target) target.innerHTML = html.querySelector(selector).innerHTML;
      });
      var mobileForm = document.getElementById('FacetFiltersFormMobile');
      if (mobileForm) {
        var drawer = mobileForm.closest('menu-drawer');
        if (drawer && typeof drawer.bindEvents === 'function') drawer.bindEvents();
      }
    };

    /* Refresh the CLICKED filter card too.
       facets.js's `renderFilters` deliberately SKIPS the just-clicked
       `.js-filter` (it routes that one through `renderCounts` /
       `renderMobileCounts` which only touch `.facets__summary` and
       `.mobile-facets__list` — neither of which we render). Result:
       after a click, every OTHER card refreshes but the clicked card's
       active row never gets the `.is-active` styling.
       Patch wraps `renderFilters` to also swap the clicked card's
       innerHTML from the fetched HTML so its rows show the new active
       state immediately. Important for combined TYPE / SIZE cards.
       Skip the swap when the event came from a form input element —
       the user might still be typing (price range), and replacing the
       input would erase their value and steal focus mid-keystroke. */
    var origRenderFilters = FacetFiltersForm.renderFilters;
    FacetFiltersForm.renderFilters = function (html, event) {
      origRenderFilters.call(FacetFiltersForm, html, event);
      if (!event || !event.target || !event.target.closest) return;
      var tag = event.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      var clicked = event.target.closest('.js-filter');
      if (!clicked || !clicked.id) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var fetchedClicked = parsed.getElementById(clicked.id);
      if (fetchedClicked) clicked.innerHTML = fetchedClicked.innerHTML;
    };

    /* Route price-form submissions through their OWN FormData.
       Stock `onSubmitHandler` (facets.js ~268) only includes forms
       with id EXACTLY 'FacetSortForm' / 'FacetFiltersForm' /
       'FacetSortDrawerForm'. Our price form has its own per-section
       id (`FacetPriceForm-…`), so its typed values get silently
       dropped → URL goes out without `filter.v.price.gte/lte` →
       response renders the input empty.
       Use a document-level capture-phase `input` listener that fires
       BEFORE the per-form listener facets.js adds in its constructor.
       For events from our price form, debounce + build the URL from
       that form's FormData and call `renderPage` directly, then
       `stopImmediatePropagation` so facets.js's broken handler is
       skipped. This avoids touching `FacetFiltersForm.prototype` —
       which has been observed to be inaccessible in some build
       environments. */
    if (!document.documentElement.dataset.hhsPriceFormBound) {
      document.documentElement.dataset.hhsPriceFormBound = '1';
      var priceDebounceTimer = null;
      document.addEventListener('input', function (e) {
        if (!e.target || !e.target.closest) return;
        var form = e.target.closest('form');
        if (!form || !form.id || form.id.indexOf('FacetPriceForm') !== 0) return;
        e.stopImmediatePropagation();
        clearTimeout(priceDebounceTimer);
        priceDebounceTimer = setTimeout(function () {
          var fd = new FormData(form);
          var sp = new URLSearchParams(fd).toString();
          if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.renderPage) {
            FacetFiltersForm.renderPage(sp, e);
          }
        }, 800);
      }, true);
    }

    /* Reveal newly-injected `.fu` cards.
       `hh-studio.js` runs `revealScan(document)` ONCE at load and uses an
       IntersectionObserver to add `.vis` when each element enters the
       viewport. The observer's closure is private to that file's IIFE, so
       once facets.js swaps `#ProductGridContainer.innerHTML` the new cards
       carry `.fu` (opacity:0, translateY(22px)) but no observer is watching
       them — they stay invisible.
       The user is already looking at the grid when they filter, so the
       semantically correct state is "already revealed". Wrap
       `renderProductGridContainer` to flip `.fu` → `.fu.vis` on every new
       node inside the container after the swap. */
    var origRenderGrid = FacetFiltersForm.renderProductGridContainer;
    FacetFiltersForm.renderProductGridContainer = function (html) {
      origRenderGrid.call(FacetFiltersForm, html);
      var container = document.getElementById('ProductGridContainer');
      if (!container) return;
      /* Reveal `.fu` fade-up cards (see comment above). */
      container.querySelectorAll('.fu:not(.vis)').forEach(function (el) {
        el.classList.add('vis');
      });
      /* Re-bind Quick View on the freshly-swapped cards. The open
         buttons (`[data-qv-open]`) and the modals (`.hhs-qv` with their
         close buttons + variant pickers) all live INSIDE the product
         card snippet, so `#ProductGridContainer.innerHTML = …` destroys
         their previous listeners. `dataset.bound` guards in these init
         functions prevent double-binding for any survivors. */
      if (typeof initQuickViewOpeners === 'function') initQuickViewOpeners(container);
      if (typeof initQuickViewVariantSync === 'function') initQuickViewVariantSync(container);
    };

    FacetFiltersForm._hhsPatched = true;
    return true;
  }
  if (!patchFacetsRenderAdditionalElements()) {
    /* facets.js loads with defer too; in source order it runs before us,
       but be defensive in case load order changes (preload, async tag). */
    document.addEventListener('DOMContentLoaded', patchFacetsRenderAdditionalElements);
  }

  /* ─── Hero slideshow auto-rotate + dot navigation ─── */
  function initHeroSlideshow(root) {
    var hero = (root || document).querySelector('.coll-hero');
    if (!hero || hero.dataset.bound === '1') return;
    var slides = Array.from(hero.querySelectorAll('.coll-hero-slide'));
    var dots = Array.from(hero.querySelectorAll('.coll-hdot'));
    if (slides.length < 2) { hero.dataset.bound = '1'; return; }
    hero.dataset.bound = '1';

    var idx = 0;
    var interval = parseInt(hero.dataset.interval || '6', 10) * 1000;

    function go(i) {
      idx = ((i % slides.length) + slides.length) % slides.length;
      slides.forEach(function (s, n) { s.classList.toggle('act', n === idx); });
      dots.forEach(function (d, n) {
        var on = n === idx;
        d.classList.toggle('act', on);
        d.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    dots.forEach(function (d, n) {
      d.addEventListener('click', function () {
        go(n);
        resetTimer();
      });
    });

    var timer = null;
    function resetTimer() {
      if (timer) clearInterval(timer);
      if (interval > 0) timer = setInterval(function () { go(idx + 1); }, interval);
    }
    resetTimer();

    /* Pause on hover for accessibility / motion preference */
    hero.addEventListener('mouseenter', function () { if (timer) { clearInterval(timer); timer = null; } });
    hero.addEventListener('mouseleave', resetTimer);

    /* Re-arm after a bfcache restore (back/forward navigation): the page
       returns with timers cleared but `dataset.bound` still set, so re-init
       is skipped — restart the rotation explicitly. */
    window.addEventListener('pageshow', function (e) { if (e.persisted) resetTimer(); });
  }

  /* ─── Filter sidebar / drawer accordion + open/close ─── */

  /* Card-header toggle — single delegated listener on document.
     Each `.fsb-card` header (`.fsb-head`) toggles `.open` on its
     parent card. Lives in BOTH desktop sidebar (`#FacetFiltersForm`)
     and mobile drawer (`#FacetFiltersFormMobile`). Because facets.js
     swaps `.js-filter` innerHTML on every AJAX update, per-element
     binding would need re-init + risks duplicates. Document-level
     delegation survives every swap with zero re-init.
     Clicks on the X clear button (`.fsb-x`) inside the header are
     skipped so the X only clears the filter — it doesn't also toggle
     the card. */
  if (!document.documentElement.dataset.hhsFsbToggleBound) {
    document.documentElement.dataset.hhsFsbToggleBound = '1';
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      // Skip clicks on the X clear button / its facet-remove wrapper.
      if (e.target.closest('.fsb-x') || e.target.closest('facet-remove')) {
        return;
      }
      var head = e.target.closest('.fsb-head');
      if (!head) return;
      var grp = head.closest('[data-fsb-group]');
      if (!grp) return;
      var opened = grp.classList.toggle('open');
      head.setAttribute('aria-expanded', opened ? 'true' : 'false');
    });
  }

  function initFilterUI(root) {
    root = root || document;

    /* Mobile drawer open/close */
    root.querySelectorAll('[data-filter-toggle]').forEach(function (btn) {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', function () {
        var targetId = btn.getAttribute('aria-controls');
        var drawer = document.getElementById(targetId);
        if (!drawer) return;
        drawer.classList.add('open');
        drawer.setAttribute('aria-hidden', 'false');
        btn.setAttribute('aria-expanded', 'true');
        document.documentElement.style.overflow = 'hidden';
      });
    });

    root.querySelectorAll('[data-filter-close]').forEach(function (el) {
      if (el.dataset.bound === '1') return;
      el.dataset.bound = '1';
      el.addEventListener('click', function (e) {
        if (el.tagName === 'A') e.preventDefault();
        var drawer = el.closest('.filter-drawer');
        if (!drawer) return;
        drawer.classList.remove('open');
        drawer.setAttribute('aria-hidden', 'true');
        document.documentElement.style.overflow = '';
        var openerId = drawer.id;
        document.querySelectorAll('[data-filter-toggle][aria-controls="' + openerId + '"]')
          .forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      });
    });

    /* Close drawer on Escape */
    if (!document.documentElement.dataset.hhsFilterEsc) {
      document.documentElement.dataset.hhsFilterEsc = '1';
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        document.querySelectorAll('.filter-drawer.open').forEach(function (d) {
          d.classList.remove('open');
          d.setAttribute('aria-hidden', 'true');
          document.documentElement.style.overflow = '';
        });
      });
    }
  }

  /* Custom sort-form submit handler — DISABLED.
     The sort `<select>` is now inside Craft's `<facet-filters-form>` web
     element. `facets.js` listens to `input` events on the form and
     AJAX-renders the grid (no full-page reload). Calling form.submit()
     ourselves would short-circuit the AJAX flow and trigger a reload —
     the exact bug this refactor is fixing.

     Kept as a no-op so any old call site doesn't crash. To re-enable
     the legacy form.submit() behavior, restore the body inside the
     commented block below. */
  function initSortForm(root) {
    void root;
    /* COMMENTED OUT — was forcing full page reload on sort change.
    (root || document).querySelectorAll('[data-hhs-sort-form]').forEach(function (form) {
      if (form.dataset.bound === '1') return;
      form.dataset.bound = '1';
      var sel = form.querySelector('select[name="sort_by"]');
      if (!sel) return;
      sel.addEventListener('change', function () { form.submit(); });
    });
    */
  }

  /* Collection filtering / sorting / pagination / switching use native
     full-page navigation. Every filter row is a real `<a href>` with
     `value.url_to_add` / `value.url_to_remove`; pagination uses Shopify's
     `paginate | default_pagination` links; tabs use real collection URLs;
     the sort dropdown auto-submits its `<form method="get">`. No AJAX
     section-swap layer — Shopify renders the collection with the new
     state on each navigation, which is the most reliable theme-native
     pattern and the one Shopify designed `collection.filters`,
     `paginate`, and `collection.url` to support.

     (The previous custom `initCollectionAjax` block was removed because
     its `currentGrid.replaceWith(newGrid)` swap was silently no-op'ing
     under some Shopify responses, leaving the page in a dimmed
     "loading" state with no products visible. Restoring native
     navigation fixes filter, pagination, and collection switching all
     in one move and removes ~250 lines of fragile glue.) */
  function initCollectionAjax(root) {
    /* No-op. Native full-page navigation handles filter/sort/pagination
       /collection-switching via real `<a href>` URLs and the auto-submit
       sort form (see initSortForm). Kept as a function so any future
       call site doesn't crash. */
    void root;
  }

  /* ─── Quick-view modal: open/close + variant-card sync ─── */
  function openQuickView(modal, openerBtn) {
    if (!modal) return;
    modal._opener = openerBtn || document.activeElement;
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('hhs-qv-open');
    var panel = modal.querySelector('.hhs-qv-panel');
    if (panel) setTimeout(function () { try { panel.focus(); } catch (e) {} }, 50);
  }
  function closeQuickView(modal) {
    if (!modal || !modal.classList.contains('open')) return;
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.hhs-qv.open')) {
      document.body.classList.remove('hhs-qv-open');
    }
    if (modal._opener && typeof modal._opener.focus === 'function') {
      try { modal._opener.focus(); } catch (e) {}
    }
  }
  function initQuickViewOpeners(root) {
    (root || document).querySelectorAll('[data-qv-open]').forEach(function (btn) {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var id = btn.getAttribute('data-qv-open');
        var modal = document.getElementById(id);
        openQuickView(modal, btn);
      });
    });

    /* Close handlers — once per modal */
    (root || document).querySelectorAll('[data-qv-modal]').forEach(function (modal) {
      if (modal.dataset.bound === '1') return;
      modal.dataset.bound = '1';
      modal.querySelectorAll('[data-qv-close]').forEach(function (closer) {
        closer.addEventListener('click', function (e) {
          e.preventDefault();
          closeQuickView(modal);
        });
      });
    });

    /* Global Escape — bind once */
    if (!document.documentElement.dataset.hhsQvEsc) {
      document.documentElement.dataset.hhsQvEsc = '1';
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        var open = document.querySelector('.hhs-qv.open');
        if (open) closeQuickView(open);
      });
    }

    /* Auto-close any open QV modal after a successful add-to-cart.
       Craft's `product-form.js` only auto-closes elements that are
       `<quick-add-modal>` (its own Dawn-native modal). Our HHS modal
       is `<div class="hhs-qv">`, which Craft doesn't recognise — so
       the modal would otherwise stay open after the AJAX submit and
       the cart-notification would slide in BEHIND it, plus focus
       management would jump the page to the top of the cart-notification.
       Subscribing to the `cartUpdate` pub/sub event (which Craft fires
       at the end of every successful add) lets us close the modal at
       exactly the right moment without monkey-patching product-form.js. */
    if (!document.documentElement.dataset.hhsQvCartSub &&
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      document.documentElement.dataset.hhsQvCartSub = '1';
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
        // Only react to adds that came from a product-form inside our QV
        // modal (filters out adds from the PDP main form, featured product,
        // collection-card quick-add, also-like quick-add, etc.).
        if (!data || data.source !== 'product-form') return;
        document.querySelectorAll('.hhs-qv.open').forEach(function (m) {
          if (m.querySelector('product-form')) closeQuickView(m);
        });
      });
    }
  }

  /* Variant-card sync inside each quick-view modal */
  function initQuickViewVariantSync(root) {
    (root || document).querySelectorAll('.hhs-qv').forEach(function (modal) {
      if (modal.dataset.boundVariants === '1') return;
      var pid = (modal.id || '').replace('hhs-qv-', '');
      if (!pid) return;
      var jsonEl = modal.querySelector('#hhs-qv-variants-' + pid);
      if (!jsonEl) return;
      var variants;
      try { variants = JSON.parse(jsonEl.textContent || '[]'); } catch (e) { return; }
      if (!variants || !variants.length) return;
      modal.dataset.boundVariants = '1';

      var hiddenId = modal.querySelector('#hhs-qv-variant-id-' + pid);
      var priceEl  = modal.querySelector('#hhs-qv-price-' + pid + ' .hhs-qv-price-amount');
      var compareEl = modal.querySelector('#hhs-qv-price-' + pid + ' .hhs-qv-price-compare');
      var atcBtn   = modal.querySelector('#hhs-qv-atc-' + pid);
      var atcTxt   = modal.querySelector('#hhs-qv-atc-txt-' + pid);
      var vlbl     = modal.querySelector('#hhs-qv-vname-' + pid);
      var img      = modal.querySelector('#hhs-qv-img-' + pid);
      var cards    = modal.querySelectorAll('.hhs-qv-card');
      var baseAtcText = atcTxt ? atcTxt.textContent.trim() : 'Add to cart';

      function pickByCard(card) {
        if (card.classList.contains('unavailable')) return;
        var vid = card.dataset.variantId;
        var v = variants.find(function (x) { return String(x.id) === String(vid); });
        if (!v) return;

        if (hiddenId) hiddenId.value = v.id;

        cards.forEach(function (c) {
          var isSel = String(c.dataset.variantId) === String(v.id);
          c.classList.toggle('sel', isSel);
          var inp = c.querySelector('input.sr');
          if (inp) inp.checked = isSel;
        });

        if (priceEl) priceEl.textContent = card.dataset.price || priceEl.textContent;
        if (compareEl) compareEl.style.display = (v.compare_at_price && v.compare_at_price > v.price) ? '' : 'none';

        if (atcBtn) {
          if (v.available) {
            atcBtn.disabled = false;
            atcBtn.removeAttribute('aria-disabled');
            if (atcTxt) atcTxt.textContent = baseAtcText;
          } else {
            atcBtn.disabled = true;
            atcBtn.setAttribute('aria-disabled', 'true');
            if (atcTxt) atcTxt.textContent = 'Sold out';
          }
        }

        if (vlbl) vlbl.textContent = v.title || '';

        var imgSrc = card.dataset.image;
        if (img && imgSrc) {
          img.src = imgSrc;
          img.alt = (v.title || '') + ' — ' + (img.dataset.baseAlt || '');
        }
      }

      cards.forEach(function (c) {
        c.addEventListener('click', function () { pickByCard(c); });
        var inp = c.querySelector('input.sr');
        if (inp) inp.addEventListener('change', function () { if (inp.checked) pickByCard(c); });
      });

      /* qty +/- */
      var qtyInput = modal.querySelector('.hhs-qv-qty-val');
      var minusBtn = modal.querySelector('[data-qv-qty-minus]');
      var plusBtn  = modal.querySelector('[data-qv-qty-plus]');
      if (minusBtn && qtyInput) minusBtn.addEventListener('click', function () {
        var v = parseInt(qtyInput.value, 10) || 1; qtyInput.value = Math.max(1, v - 1);
      });
      if (plusBtn && qtyInput) plusBtn.addEventListener('click', function () {
        var v = parseInt(qtyInput.value, 10) || 1; qtyInput.value = v + 1;
      });
    });
  }

  function initAll(root) {
    initHeroSlideshow(root);
    initFilterUI(root);
    initSortForm(root);
    initQuickViewOpeners(root);
    initQuickViewVariantSync(root);
    initCollectionAjax(root);
  }

  function boot() { initAll(document); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* Safety net: re-run after full load in case the first pass raced with
     section rendering on a cold (uncached) first load — a deferred-script
     timing edge that left the hero slideshow occasionally un-started, while
     a refresh (warm cache) worked. Every init function guards with
     `dataset.bound`, so this extra pass is idempotent (no double timers). */
  window.addEventListener('load', boot);

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
